/**
 * ComposableBlockInspector - Right-panel inspector for composable blocks
 * 
 * Registry-driven: renders fields based on block type schema.
 * Supports: heading, paragraph, button, image, section, container
 * 
 * Text fields debounce (600ms) before committing to avoid spamming undo history.
 * Selects/toggles commit immediately.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Type, AlignLeft, AlignCenter, AlignRight, Image as ImageIcon,
  Layers, Box, Link2, ExternalLink, Upload, MousePointer,
} from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { cn } from '../../lib/utils';
import {
  ALL_BLOCK_TYPES, TEXT_BLOCK_TYPES, MEDIA_BLOCK_TYPES, LAYOUT_BLOCK_TYPES,
} from './AddSectionPanel';

// ============================================================================
// DEBOUNCED INPUT — updates local state immediately, commits after delay/blur
// ============================================================================

const DebouncedInput = ({ value, onCommit, delay = 600, ...rest }) => {
  const [local, setLocal] = useState(value ?? '');
  const timerRef = useRef(null);

  useEffect(() => { setLocal(value ?? ''); }, [value]);

  const flush = useCallback((val) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    onCommit(val);
  }, [onCommit]);

  const handleChange = (e) => {
    const val = e.target.value;
    setLocal(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(val), delay);
  };

  const handleBlur = () => flush(local);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return <Input value={local} onChange={handleChange} onBlur={handleBlur} {...rest} />;
};

const DebouncedTextarea = ({ value, onCommit, delay = 600, ...rest }) => {
  const [local, setLocal] = useState(value ?? '');
  const timerRef = useRef(null);

  useEffect(() => { setLocal(value ?? ''); }, [value]);

  const flush = useCallback((val) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    onCommit(val);
  }, [onCommit]);

  const handleChange = (e) => {
    const val = e.target.value;
    setLocal(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flush(val), delay);
  };

  const handleBlur = () => flush(local);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return <Textarea value={local} onChange={handleChange} onBlur={handleBlur} {...rest} />;
};

// ============================================================================
// ALIGNMENT BUTTONS
// ============================================================================

const AlignmentPicker = ({ value, onChange }) => {
  const options = [
    { val: 'left', icon: AlignLeft },
    { val: 'center', icon: AlignCenter },
    { val: 'right', icon: AlignRight },
  ];
  return (
    <div className="flex gap-1" data-testid="alignment-picker">
      {options.map(({ val, icon: Icon }) => (
        <button
          key={val}
          onClick={() => onChange(val)}
          className={cn(
            'p-1.5 rounded transition-colors',
            value === val
              ? 'bg-orange-500/20 text-orange-400'
              : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
          )}
          title={val}
          data-testid={`align-${val}`}
        >
          <Icon className="w-4 h-4" />
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// BLOCK-SPECIFIC FIELD SETS
// ============================================================================

const HeadingFields = ({ props, onChange }) => (
  <div className="space-y-4" data-testid="heading-inspector-fields">
    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Heading Text</Label>
      <DebouncedInput
        value={props.text}
        onCommit={(v) => onChange('text', v)}
        placeholder="Section Heading"
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
        data-testid="heading-text-input"
      />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Style Preset</Label>
      <Select value={props.level || 'h2'} onValueChange={(v) => onChange('level', v)}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]" data-testid="heading-level-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          <SelectItem value="h1">H1 — Page Title</SelectItem>
          <SelectItem value="h2">H2 — Section</SelectItem>
          <SelectItem value="h3">H3 — Subsection</SelectItem>
          <SelectItem value="h4">H4 — Small</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Alignment</Label>
      <AlignmentPicker value={props.alignment || 'left'} onChange={(v) => onChange('alignment', v)} />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Color</Label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={props.color || '#ffffff'}
          onChange={(e) => onChange('color', e.target.value)}
          className="w-8 h-8 rounded border border-zinc-700 bg-transparent cursor-pointer"
          data-testid="heading-color-input"
        />
        <DebouncedInput
          value={props.color || '#ffffff'}
          onCommit={(v) => onChange('color', v)}
          className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px] flex-1 font-mono"
        />
      </div>
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Max Width</Label>
      <DebouncedInput
        value={props.maxWidth || ''}
        onCommit={(v) => onChange('maxWidth', v)}
        placeholder="e.g. 800px or 100%"
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
      />
    </div>
  </div>
);

const ParagraphFields = ({ props, onChange }) => (
  <div className="space-y-4" data-testid="paragraph-inspector-fields">
    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Content</Label>
      <DebouncedTextarea
        value={props.text}
        onCommit={(v) => onChange('text', v)}
        placeholder="Add your content here..."
        className="bg-zinc-900 border-zinc-800 text-white text-[12px] min-h-[100px] resize-y"
        data-testid="paragraph-text-input"
      />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Alignment</Label>
      <AlignmentPicker value={props.alignment || 'left'} onChange={(v) => onChange('alignment', v)} />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Color</Label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={props.color || '#a1a1aa'}
          onChange={(e) => onChange('color', e.target.value)}
          className="w-8 h-8 rounded border border-zinc-700 bg-transparent cursor-pointer"
          data-testid="paragraph-color-input"
        />
        <DebouncedInput
          value={props.color || '#a1a1aa'}
          onCommit={(v) => onChange('color', v)}
          className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px] flex-1 font-mono"
        />
      </div>
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Max Width</Label>
      <DebouncedInput
        value={props.maxWidth || ''}
        onCommit={(v) => onChange('maxWidth', v)}
        placeholder="e.g. 640px"
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
      />
    </div>
  </div>
);

const ButtonFields = ({ props, onChange }) => (
  <div className="space-y-4" data-testid="button-inspector-fields">
    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Label</Label>
      <DebouncedInput
        value={props.text}
        onCommit={(v) => onChange('text', v)}
        placeholder="Click Me"
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
        data-testid="button-text-input"
      />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Link URL</Label>
      <DebouncedInput
        value={props.url}
        onCommit={(v) => onChange('url', v)}
        placeholder="https://... or /page"
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
        data-testid="button-url-input"
      />
    </div>

    <div className="flex items-center justify-between">
      <Label className="text-zinc-400 text-[11px]">Open in New Tab</Label>
      <Switch
        checked={props.openInNewTab || false}
        onCheckedChange={(v) => onChange('openInNewTab', v)}
        data-testid="button-new-tab-toggle"
      />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Variant</Label>
      <Select value={props.variant || 'primary'} onValueChange={(v) => onChange('variant', v)}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]" data-testid="button-variant-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          <SelectItem value="primary">Primary</SelectItem>
          <SelectItem value="secondary">Secondary</SelectItem>
          <SelectItem value="outline">Outline</SelectItem>
          <SelectItem value="ghost">Ghost</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Size</Label>
      <Select value={props.size || 'medium'} onValueChange={(v) => onChange('size', v)}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]" data-testid="button-size-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          <SelectItem value="small">Small</SelectItem>
          <SelectItem value="medium">Medium</SelectItem>
          <SelectItem value="large">Large</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Alignment</Label>
      <AlignmentPicker value={props.alignment || 'left'} onChange={(v) => onChange('alignment', v)} />
    </div>
  </div>
);

const ImageFields = ({ props, onChange }) => (
  <div className="space-y-4" data-testid="image-inspector-fields">
    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Image URL</Label>
      <DebouncedInput
        value={props.src}
        onCommit={(v) => onChange('src', v)}
        placeholder="Paste image URL or path..."
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
        data-testid="image-src-input"
      />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Alt Text</Label>
      <DebouncedInput
        value={props.alt}
        onCommit={(v) => onChange('alt', v)}
        placeholder="Describe the image"
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
        data-testid="image-alt-input"
      />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Fit</Label>
      <Select value={props.objectFit || 'cover'} onValueChange={(v) => onChange('objectFit', v)}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]" data-testid="image-fit-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          <SelectItem value="cover">Cover</SelectItem>
          <SelectItem value="contain">Contain</SelectItem>
          <SelectItem value="fill">Fill</SelectItem>
          <SelectItem value="none">None (natural size)</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Border Radius</Label>
      <Select value={props.borderRadius || 'md'} onValueChange={(v) => onChange('borderRadius', v)}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]" data-testid="image-radius-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          <SelectItem value="none">None</SelectItem>
          <SelectItem value="sm">Small (4px)</SelectItem>
          <SelectItem value="md">Medium (8px)</SelectItem>
          <SelectItem value="lg">Large (16px)</SelectItem>
          <SelectItem value="xl">XL (24px)</SelectItem>
          <SelectItem value="full">Full / Circle</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Link URL (optional)</Label>
      <DebouncedInput
        value={props.linkUrl || ''}
        onCommit={(v) => onChange('linkUrl', v)}
        placeholder="https://..."
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
      />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Max Height</Label>
      <DebouncedInput
        value={props.maxHeight || ''}
        onCommit={(v) => onChange('maxHeight', v)}
        placeholder="e.g. 400px"
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
      />
    </div>
  </div>
);

const SectionLayoutFields = ({ props, onChange }) => (
  <div className="space-y-4" data-testid="section-inspector-fields">
    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Background Color</Label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={(props.backgroundColor && props.backgroundColor !== 'transparent') ? props.backgroundColor : '#000000'}
          onChange={(e) => onChange('backgroundColor', e.target.value)}
          className="w-8 h-8 rounded border border-zinc-700 bg-transparent cursor-pointer"
          data-testid="section-bg-color-input"
        />
        <DebouncedInput
          value={props.backgroundColor || 'transparent'}
          onCommit={(v) => onChange('backgroundColor', v)}
          className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px] flex-1 font-mono"
        />
      </div>
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Background Image</Label>
      <DebouncedInput
        value={props.backgroundImage || ''}
        onCommit={(v) => onChange('backgroundImage', v)}
        placeholder="https://..."
        className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
      />
    </div>

    <div className="space-y-1.5">
      <div className="flex justify-between">
        <Label className="text-zinc-400 text-[11px]">Padding Top</Label>
        <span className="text-[11px] text-zinc-500">{props.paddingTop ?? 48}px</span>
      </div>
      <Slider
        value={[props.paddingTop ?? 48]}
        onValueChange={([v]) => onChange('paddingTop', v)}
        min={0} max={128} step={4}
        className="py-1"
      />
    </div>

    <div className="space-y-1.5">
      <div className="flex justify-between">
        <Label className="text-zinc-400 text-[11px]">Padding Bottom</Label>
        <span className="text-[11px] text-zinc-500">{props.paddingBottom ?? 48}px</span>
      </div>
      <Slider
        value={[props.paddingBottom ?? 48]}
        onValueChange={([v]) => onChange('paddingBottom', v)}
        min={0} max={128} step={4}
        className="py-1"
      />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Max Width</Label>
      <Select value={props.maxWidth || '1280px'} onValueChange={(v) => onChange('maxWidth', v)}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          <SelectItem value="100%">Full Width</SelectItem>
          <SelectItem value="1440px">1440px</SelectItem>
          <SelectItem value="1280px">1280px</SelectItem>
          <SelectItem value="1024px">1024px</SelectItem>
          <SelectItem value="768px">768px</SelectItem>
        </SelectContent>
      </Select>
    </div>
  </div>
);

const ContainerFields = ({ props, onChange }) => (
  <div className="space-y-4" data-testid="container-inspector-fields">
    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Max Width</Label>
      <Select value={props.maxWidth || '1200px'} onValueChange={(v) => onChange('maxWidth', v)}>
        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-zinc-900 border-zinc-800">
          <SelectItem value="100%">Full Width</SelectItem>
          <SelectItem value="1440px">1440px</SelectItem>
          <SelectItem value="1200px">1200px</SelectItem>
          <SelectItem value="960px">960px</SelectItem>
          <SelectItem value="640px">640px</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-1.5">
      <div className="flex justify-between">
        <Label className="text-zinc-400 text-[11px]">Padding</Label>
        <span className="text-[11px] text-zinc-500">{props.padding ?? 24}px</span>
      </div>
      <Slider
        value={[props.padding ?? 24]}
        onValueChange={([v]) => onChange('padding', v)}
        min={0} max={96} step={4}
        className="py-1"
      />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Alignment</Label>
      <AlignmentPicker value={props.alignment || 'center'} onChange={(v) => onChange('alignment', v)} />
    </div>

    <div className="space-y-1.5">
      <Label className="text-zinc-400 text-[11px]">Background Color</Label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={(props.backgroundColor && props.backgroundColor !== 'transparent') ? props.backgroundColor : '#000000'}
          onChange={(e) => onChange('backgroundColor', e.target.value)}
          className="w-8 h-8 rounded border border-zinc-700 bg-transparent cursor-pointer"
        />
        <DebouncedInput
          value={props.backgroundColor || 'transparent'}
          onCommit={(v) => onChange('backgroundColor', v)}
          className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px] flex-1 font-mono"
        />
      </div>
    </div>
  </div>
);

// ============================================================================
// BLOCK TYPE → ICON MAP
// ============================================================================

const BLOCK_ICON_MAP = {
  heading: Type,
  paragraph: AlignLeft,
  button: MousePointer,
  image: ImageIcon,
  section: Layers,
  container: Box,
  gallery_grid: ImageIcon,
};

// ============================================================================
// COMPOSABLE BLOCK INSPECTOR — dispatches to per-type field set
// ============================================================================

const COMPOSABLE_TYPES = new Set([
  'heading', 'paragraph', 'button', 'image', 'section', 'container', 'gallery_grid',
]);

export function isComposableBlock(type) {
  return COMPOSABLE_TYPES.has(type);
}

const ComposableBlockInspector = ({ section, onUpdateProps }) => {
  if (!section || !isComposableBlock(section.type)) return null;

  const { type } = section;
  const props = section.props || {};
  const blockDef = ALL_BLOCK_TYPES[type];
  const Icon = BLOCK_ICON_MAP[type] || Layers;

  const handleChange = (key, value) => {
    onUpdateProps(key, value);
  };

  const renderFields = () => {
    switch (type) {
      case 'heading':   return <HeadingFields props={props} onChange={handleChange} />;
      case 'paragraph': return <ParagraphFields props={props} onChange={handleChange} />;
      case 'button':    return <ButtonFields props={props} onChange={handleChange} />;
      case 'image':     return <ImageFields props={props} onChange={handleChange} />;
      case 'section':   return <SectionLayoutFields props={props} onChange={handleChange} />;
      case 'container': return <ContainerFields props={props} onChange={handleChange} />;
      default:          return <p className="text-zinc-500 text-sm px-4">No fields for {type}</p>;
    }
  };

  return (
    <aside
      className="w-[260px] bg-zinc-950 border-l border-zinc-800/30 flex flex-col h-full"
      data-testid="composable-block-inspector"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800/30">
        <h2 className="text-[11px] font-medium text-zinc-400 tracking-wide">CONFIGURE</h2>
      </div>

      {/* Block identifier */}
      <div className="px-4 py-3 border-b border-zinc-800/30 bg-zinc-900/30">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-orange-500/20 rounded">
            <Icon className="w-4 h-4 text-orange-400" />
          </div>
          <div>
            <div className="text-[13px] font-medium text-white">{blockDef?.name || type}</div>
            <div className="text-[11px] text-zinc-500">{blockDef?.description || 'Block element'}</div>
          </div>
        </div>
      </div>

      {/* Fields */}
      <div className="flex-1 overflow-y-auto p-4">
        {renderFields()}
      </div>

      {/* Enabled toggle + Block ID */}
      <div className="p-4 border-t border-zinc-800/30 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-400 text-[11px]">Enabled</Label>
          <Switch
            checked={section.enabled !== false}
            onCheckedChange={(v) => onUpdateProps('__enabled', v)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-zinc-500 text-[10px]">Block ID</Label>
          <p className="text-[10px] font-mono text-zinc-600 truncate">{section.id}</p>
        </div>
      </div>
    </aside>
  );
};

export default ComposableBlockInspector;
